import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  RefObject,
} from "react";
import { ColumnLayout, Header } from "@amzn/awsui-components-react-v3";
import {
  FieldGroupDto,
  FieldSchemaDto,
  EditComponentErrorGroup,
} from "@amzn/crowd-front-end-common-library";
import WizardFooter from "../../wizard/WizardFooter/WizardFooter";
import { FormContextProvider } from "../../forms";
import createTeamStyle from "../createTeam.module.css";
import "../../forms/FormGroupSection/FormGroupSection.module.css";
import { CreateTeamContext } from "../CreateTeamContext";
import { useWizardWorkflowContext } from "../../wizard/Wizard/WizardWorkflowContextProvider";
import { CustomStepInjectedProps } from "../../wizard/WizardStep/WizardStep";
import useCreateTeamValidate from "./useCreateTeamValidate";
import { LoadingState } from "../../http";
import FormFieldRenderer from "../../forms/FormFieldRenderer/FormFieldRenderer";
import { CreateTeamInputStepResult as InputStepResult } from "./CreateTeamInputStepResult";
import styles from "../../common/loader.module.css";
import InheritedAccess from "../inheritedAccess/inheritedAccessComponent";

const CreateTeamInputStep: React.FC<CustomStepInjectedProps<
  Partial<InputStepResult>
>> = ({ stepResult, setStepResult, goNext }): JSX.Element => {
  const { parentTeamId, schema, metrics } = useWizardWorkflowContext<
    CreateTeamContext
  >();
  const [localChanges, setLocalChanges] = useState<Partial<InputStepResult>>(
    stepResult
  );

  const flashRef = useRef<HTMLDivElement>(null);

  const updateLocalChanges = useCallback(
    (_: Partial<InputStepResult>, diff: Partial<InputStepResult>) => {
      setLocalChanges(diff);
    },
    []
  );
  const onPassValidation = useCallback(
    (validatedChanges: Partial<InputStepResult>) => {
      setStepResult(validatedChanges);
      goNext();
    },
    [setStepResult, goNext]
  );

  const {
    execute,
    loadingState,
    validationErrors,
    thrownValidationError,
  } = useCreateTeamValidate(
    schema,
    parentTeamId,
    localChanges,
    metrics,
    onPassValidation
  );

  const onNext = (): void => {
    execute();
  };

  const formFieldRefs = useRef<Map<string, RefObject<HTMLElement>>>(
    new Map<string, RefObject<HTMLElement>>()
  );

  useEffect(() => {
    if (validationErrors) {
      const errors = (validationErrors.errors as EditComponentErrorGroup[]).flatMap(
        (errorGroup) => errorGroup.errors
      );
      const ref =
        errors.length > 0
          ? formFieldRefs.current.get(errors[0].fieldName)
          : null;
      if (ref && ref.current) {
        ref.current.scrollIntoView();
      }
    }
  }, [validationErrors]);

  const teamGroup = schema.groups.find(
    (group) => group.groupLabel === "Team details"
  ) as FieldGroupDto;
  const renderedFieldDefinitions = teamGroup.fieldDefinitions;

  const getFieldRenderer = (field: FieldSchemaDto): JSX.Element => {
    if (field.label === "Team owner") {
      const descriptionText = "The team owner will be granted admin access.";
      return (
        <FormFieldRenderer
          key={field.propertyName}
          field={field}
          customLabel={descriptionText}
        />
      );
    }

    return <FormFieldRenderer key={field.propertyName} field={field} />;
  };

  const generateAndRegisterRef = (propertyName: string) => {
    const ref = React.createRef<HTMLElement>();
    formFieldRefs.current.set(propertyName, ref);
    return ref;
  };

  return (
    <React.Fragment>
      <div className={createTeamStyle["update-step-wrapper"]}>
        <FormContextProvider<Partial<InputStepResult>>
          metrics={metrics}
          formId="createTeam-page"
          onFormValueChange={updateLocalChanges}
          validationErrors={validationErrors}
          refCallback={generateAndRegisterRef}
        >
          <Header>{teamGroup.groupLabel}</Header>
          <ColumnLayout>
            <div data-awsui-column-layout-root="true">
              {renderedFieldDefinitions.map((field) => getFieldRenderer(field))}
            </div>
          </ColumnLayout>
          <InheritedAccess metrics={metrics} parentTeamId={parentTeamId} />
        </FormContextProvider>
      </div>
      <WizardFooter
        onNext={onNext}
        isLoading={loadingState === LoadingState.LOADING}
      />
    </React.Fragment>
  );
};

export default CreateTeamInputStep;
